// src/components/orders/OrderCreationModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Row, Col, Divider, Table, InputNumber, DatePicker, Space, List, Card, Badge, Empty, Alert, Typography, Upload, Spin, Switch, Segmented, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckOutlined, SearchOutlined, PaperClipOutlined, EyeOutlined, FileTextOutlined, FilePdfOutlined, FileImageOutlined, SwapOutlined, LoadingOutlined, AppstoreAddOutlined } from '@ant-design/icons';
import { getAllSuppliers } from '../../services/supplierService';
import { createPedido } from '../../services/pedidosService';
import { uploadAttachment, validateFile, ATTACHMENT_CONSTRAINTS } from '../../services/orderAttachmentsService';
import { searchProductsBySupplier, checkProductDuplicates } from '../../services/productsService';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import locale from 'antd/es/date-picker/locale/es_ES';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const OrderCreationModal = ({ visible, onClose, onOrderCreated }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    // Suppliers state
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(true);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    
    // Revenue Taxes (Cargas Impositivas) state
    const [cargasImpositivasOn, setCargasImpositivasOn] = useState(true); // Toggle for tax charges
    const [taxInputMode, setTaxInputMode] = useState('amount'); // 'percentage' or 'amount' - default to amount (matches invoices)
    
    // Tax amounts in $ (what gets sent to backend)
    const [retencionAmount, setRetencionAmount] = useState(0);
    const [percepcionAmount, setPercepcionAmount] = useState(0);
    const [iibbDirectoAmount, setIibbDirectoAmount] = useState(0);
    
    // Tax percentages (for percentage mode or display)
    const [retencionPct, setRetencionPct] = useState(0);
    const [percepcionPct, setPercepcionPct] = useState(0);
    const [iibbDirectoPct, setIibbDirectoPct] = useState(0);
    
    // Order items state
    const [orderItems, setOrderItems] = useState([]);
    const [searchProduct, setSearchProduct] = useState('');
    
    // Product search state
    const [searchResults, setSearchResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    
    // Duplicate check state
    const [checkingDuplicates, setCheckingDuplicates] = useState({});

    // Discount state (3-field pricing system)
    const [globalDiscount, setGlobalDiscount] = useState(0);

    // Payment method state for dynamic terms
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [selectedPaymentTerms, setSelectedPaymentTerms] = useState(null);
    const [customPaymentDate, setCustomPaymentDate] = useState(null);

    // File attachment state
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [filePreviewVisible, setFilePreviewVisible] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState({ current: 0, total: 0, percent: 0 });

    // Mode toggle state: false = Completo (default), true = Simplificado
    const [isSimplificado, setIsSimplificado] = useState(true);

    // Payment date type: ADELANTADO | CONTRAENTREGA | A_DETERMINAR
    const [paymentDateType, setPaymentDateType] = useState('A_DETERMINAR');

    // Simplificado mode: manual totals from supplier invoice
    const [simpSubtotal, setSimpSubtotal] = useState(0);
    const [simpIva, setSimpIva] = useState(0);
    const [simpTotal, setSimpTotal] = useState(0);

    // File upload configuration - use constants from service
    const allowedFileTypes = ATTACHMENT_CONSTRAINTS.ALLOWED_MIME_TYPES;
    const maxFileSize = ATTACHMENT_CONSTRAINTS.MAX_FILE_SIZE;
    const maxFiles = ATTACHMENT_CONSTRAINTS.MAX_FILES_PER_ORDER;

    // Custom product modal state
    const [customProductModalVisible, setCustomProductModalVisible] = useState(false);
    const [customProductForm] = Form.useForm();

    // Fetch suppliers on component mount
    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                setLoadingSuppliers(true);
                const response = await getAllSuppliers(1, 500, { estado: 'ACTIVO' });
                if (response && response.success) {
                    setSuppliers(response.suppliers);
                }
            } catch (error) {
                console.error('Error fetching suppliers:', error);
                message.warning('No se pudieron cargar los proveedores');
            } finally {
                setLoadingSuppliers(false);
            }
        };

        if (visible) {
            fetchSuppliers();
        }
    }, [visible]);

    // Calculate tax base (subtotal neto - after discounts, before IVA)
    const getTaxBase = () => {
        let subtotalNeto = 0;
        orderItems.forEach(item => {
            const basePrice = (item.bulk_price && item.bulk_price > 0) 
                ? item.bulk_price 
                : item.unit_price;
            const discountAmount = basePrice * (item.discount_percentage / 100);
            const netPrice = basePrice - discountAmount;
            subtotalNeto += item.quantity * netPrice;
        });
        return subtotalNeto;
    };

    // Tax conversion helpers
    const calculateAmountFromPercentage = (percentage, base) => {
        return Math.round(base * (percentage / 100) * 100) / 100;
    };

    const calculatePercentageFromAmount = (amount, base) => {
        if (base === 0) return 0;
        return Math.round((amount / base) * 100 * 100) / 100;
    };

    // Handle tax input changes with bidirectional conversion
    const handleRetencionChange = (value) => {
        const taxBase = getTaxBase();
        if (taxInputMode === 'amount') {
            setRetencionAmount(value || 0);
            setRetencionPct(calculatePercentageFromAmount(value || 0, taxBase));
        } else {
            setRetencionPct(value || 0);
            setRetencionAmount(calculateAmountFromPercentage(value || 0, taxBase));
        }
    };

    const handlePercepcionChange = (value) => {
        const taxBase = getTaxBase();
        if (taxInputMode === 'amount') {
            setPercepcionAmount(value || 0);
            setPercepcionPct(calculatePercentageFromAmount(value || 0, taxBase));
        } else {
            setPercepcionPct(value || 0);
            setPercepcionAmount(calculateAmountFromPercentage(value || 0, taxBase));
        }
    };

    const handleIibbDirectoChange = (value) => {
        const taxBase = getTaxBase();
        if (taxInputMode === 'amount') {
            setIibbDirectoAmount(value || 0);
            setIibbDirectoPct(calculatePercentageFromAmount(value || 0, taxBase));
        } else {
            setIibbDirectoPct(value || 0);
            setIibbDirectoAmount(calculateAmountFromPercentage(value || 0, taxBase));
        }
    };

    // Handle supplier selection
    const handleSupplierChange = async (supplierId) => {
        const supplier = suppliers.find(s => s.id === supplierId);
        setSelectedSupplier(supplier);
        // Clear items when supplier changes
        setOrderItems([]);
        // Clear search results
        setSearchResults([]);
        setSearchProduct('');
        setShowSearchResults(false);
        // Reset tax amounts when supplier changes
        setRetencionAmount(0);
        setPercepcionAmount(0);
        setIibbDirectoAmount(0);
        setRetencionPct(0);
        setPercepcionPct(0);
        setIibbDirectoPct(0);
    };

    // File upload handlers
    // beforeUpload: validates and prevents auto-upload to server
    const handleBeforeUpload = (file, fileList) => {
        // Validate file type
        if (!allowedFileTypes.includes(file.type)) {
            message.error('Solo se permiten archivos PDF, JPG, JPEG o PNG');
            return Upload.LIST_IGNORE; // Don't add to list
        }
        
        // Validate file size
        if (file.size > maxFileSize) {
            message.error('El archivo no debe superar 10MB');
            return Upload.LIST_IGNORE;
        }
        
        // Validate max files (current + new)
        if (attachedFiles.length >= maxFiles) {
            message.error(`Máximo ${maxFiles} archivos por pedido`);
            return Upload.LIST_IGNORE;
        }
        
        return false; // Return false to prevent auto upload, but add to list
    };

    // onChange: updates state when files are added/removed
    const handleFileChange = ({ file, fileList }) => {
        // Filter out files that failed validation
        const validFiles = fileList.filter(f => f.status !== 'error');
        setAttachedFiles(validFiles);
        
        // Show success message when file is added
        if (file.status === undefined && fileList.length > attachedFiles.length) {
            message.success(`Archivo "${file.name}" agregado`);
        }
    };

    const handleRemoveFile = (file) => {
        message.success(`Archivo "${file.name}" eliminado`);
        return true;
    };

    const handlePreviewFile = async (file) => {
        // For images, create preview URL
        if (file.type?.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewFile({
                    name: file.name,
                    type: file.type,
                    url: e.target.result
                });
                setFilePreviewVisible(true);
            };
            reader.readAsDataURL(file.originFileObj || file);
        } else if (file.type === 'application/pdf') {
            // For PDFs, show name only (can't preview without uploading)
            setPreviewFile({
                name: file.name,
                type: file.type,
                url: null
            });
            setFilePreviewVisible(true);
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType === 'application/pdf') {
            return <FilePdfOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />;
        } else if (fileType?.startsWith('image/')) {
            return <FileImageOutlined style={{ fontSize: '48px', color: '#52c41a' }} />;
        }
        return <FileTextOutlined style={{ fontSize: '48px', color: '#1890ff' }} />;
    };

    // Search products by supplier
    const handleProductSearch = async () => {
        // Validate search term length
        if (!searchProduct || searchProduct.trim().length < 2) {
            message.warning('Por favor ingrese al menos 2 caracteres para buscar');
            return;
        }

        // Don't search without a supplier
        if (!selectedSupplier) {
            message.warning('Primero seleccione un proveedor');
            return;
        }

        try {
            setSearchingProducts(true);
            const response = await searchProductsBySupplier(
                selectedSupplier.id,
                searchProduct.trim(),
                50 // Max 50 results for better UX
            );

            if (response.success) {
                setSearchResults(response.products || []);
                setShowSearchResults(true);
                
                if (response.products.length === 0) {
                    message.info('No se encontraron productos que coincidan con tu búsqueda');
                }
            }
        } catch (error) {
            console.error('Error searching products:', error);
            message.error(error.message || 'Error al buscar productos');
            setSearchResults([]);
        } finally {
            setSearchingProducts(false);
        }
    };

    // Helper to check if an item is a custom product
    const isCustomProduct = (item) => {
        if (!item.product_id) return true;
        if (typeof item.product_id === 'number' && item.product_id < 0) return true;
        if (item.product_code === 'CUSTOM') return true;
        if (item.is_custom) return true;
        return false;
    };

    // Add product from search results
    const handleSelectProduct = (product) => {
        const newItem = {
            key: Date.now(),
            product_code: product.ProductCode,
            product_description: product.ProductDescription,
            quantity: 1,
            unit_price: product.ProductCost || 0,        // Costo Base de Datos (DB reference)
            bulk_price: 0,                               // Costo Compra (supplier quote)
            discount_percentage: globalDiscount || 0,    // Inherits from global discount
            has_custom_discount: false,                  // Track if discount is customized
            iva_percentage: 21.00,
            checked: false,
            product_id: product.ProductId,
            brand: product.Brand?.BrandName || '',
            ean: product.EAN || '',
            is_custom: false
        };

        setOrderItems([...orderItems, newItem]);
        setSearchProduct('');
        setSearchResults([]);
        setShowSearchResults(false);
        message.success(`Producto ${product.ProductCode} agregado`);
    };

    // Add custom product (service, one-timer, etc.)
    const handleAddCustomProduct = (values) => {
        const newItem = {
            key: Date.now(),
            product_id: null,                           // Marks as custom product
            product_code: 'CUSTOM',                     // Visual indicator
            product_description: values.product_description.trim(),
            quantity: values.quantity,
            unit_price: values.unit_price,              // Required for custom products
            bulk_price: 0,
            discount_percentage: values.discount_percentage || 0,
            has_custom_discount: values.discount_percentage > 0,
            iva_percentage: values.iva_percentage,
            checked: false,
            brand: '-',
            ean: '',
            notes: values.notes || '',
            is_custom: true                             // Internal flag for UI
        };

        setOrderItems([...orderItems, newItem]);
        setCustomProductModalVisible(false);
        customProductForm.resetFields();
        message.success('Producto/Servicio personalizado agregado');
    };

    // Update item field
    const handleItemChange = (key, field, value) => {
        setOrderItems(orderItems.map(item => 
            item.key === key ? { ...item, [field]: value } : item
        ));
    };

    // Remove item
    const handleRemoveItem = (key) => {
        setOrderItems(orderItems.filter(item => item.key !== key));
    };

    // Handle global discount change
    const handleGlobalDiscountChange = (value) => {
        const newDiscount = Math.min(Math.max(value || 0, 0), 99.99); // Clamp 0-99.99
        setGlobalDiscount(newDiscount);

        // Apply global discount to all items that don't have custom discount
        setOrderItems(prevItems =>
            prevItems.map(item => ({
                ...item,
                discount_percentage: item.has_custom_discount 
                    ? item.discount_percentage 
                    : newDiscount
            }))
        );

        if (newDiscount > 0) {
            message.success(`Descuento global aplicado: ${newDiscount}%`);
        }
    };

    // Handle per-item discount change
    const handleDiscountChange = (key, value) => {
        const newDiscount = Math.min(Math.max(value || 0, 0), 99.99); // Clamp 0-99.99
        
        setOrderItems(prevItems =>
            prevItems.map(item => {
                if (item.key === key) {
                    // Mark as custom discount if different from global (with floating point tolerance)
                    const isCustom = Math.abs(newDiscount - globalDiscount) > 0.01;
                    return {
                        ...item,
                        discount_percentage: newDiscount,
                        has_custom_discount: isCustom
                    };
                }
                return item;
            })
        );
    };

    // Check/Verify item for duplicates
    const handleCheckItem = async (key) => {
        const item = orderItems.find(i => i.key === key);
        if (!item) return;

        // If already checked, uncheck it
        if (item.checked) {
            setOrderItems(orderItems.map(i => 
                i.key === key ? { ...i, checked: false, duplicateInfo: null } : i
            ));
            return;
        }

        // Start checking
        setCheckingDuplicates(prev => ({ ...prev, [key]: true }));

        try {
            const response = await checkProductDuplicates(
                item.product_description,
                item.product_code
            );

            if (response.success) {
                if (response.status === 'unique') {
                    // Product is unique - no message needed, just mark as checked
                    setOrderItems(orderItems.map(i => 
                        i.key === key ? { 
                            ...i, 
                            checked: true, 
                            duplicateInfo: { status: 'unique' }
                        } : i
                    ));
                } else if (response.status === 'duplicate') {
                    // Found duplicates - store info for inline display
                    setOrderItems(orderItems.map(i => 
                        i.key === key ? { 
                            ...i, 
                            checked: true, 
                            duplicateInfo: {
                                status: 'duplicate',
                                count: response.data.different_brands,
                                savings: response.data.savings_percent,
                                lowestCost: response.data.lowest_cost,
                                highestCost: response.data.highest_cost,
                                duplicates: response.data.duplicates
                            }
                        } : i
                    ));
                }
            }
        } catch (error) {
            console.error('Error checking duplicates:', error);
            message.error(error.message || 'Error al verificar duplicados');
        } finally {
            setCheckingDuplicates(prev => ({ ...prev, [key]: false }));
        }
    };

    // Calculate total with 3-field pricing and discount support
    const calculateTotal = () => {
        let subtotalBeforeIVA = 0;
        let totalIVA = 0;
        let totalDiscount = 0;
        let totalSavingsVsDatabase = 0;

        orderItems.forEach(item => {
            // Step 1: Determine base price (Costo Compra or Costo Base de Datos)
            const basePrice = (item.bulk_price && item.bulk_price > 0) 
                ? item.bulk_price 
                : item.unit_price;
            
            // Step 2: Calculate discount amount
            const discountAmount = basePrice * (item.discount_percentage / 100);
            const itemDiscountTotal = discountAmount * item.quantity;
            
            // Step 3: Calculate net price after discount
            const netPrice = basePrice - discountAmount;
            
            // Step 4: Calculate item subtotal (after discount, before IVA)
            const itemSubtotal = item.quantity * netPrice;
            
            // Step 5: Calculate IVA on discounted price (only if cargas impositivas enabled)
            const itemIva = cargasImpositivasOn ? itemSubtotal * (item.iva_percentage / 100) : 0;
            
            // Step 6: Calculate savings vs database price (if using Costo Compra)
            if (item.bulk_price && item.bulk_price > 0) {
                const savingsVsDB = (item.unit_price - netPrice) * item.quantity;
                totalSavingsVsDatabase += savingsVsDB;
            }
            
            // Accumulate totals
            subtotalBeforeIVA += itemSubtotal;
            totalIVA += itemIva;
            totalDiscount += itemDiscountTotal;
        });

        const subtotalWithIVA = subtotalBeforeIVA + totalIVA;
        
        // Use amount values directly (already set by user in either mode)
        // Only apply if cargas impositivas enabled
        const percepcion = cargasImpositivasOn ? percepcionAmount : 0;
        const iibbDirecto = cargasImpositivasOn ? iibbDirectoAmount : 0;
        const retencion = cargasImpositivasOn ? retencionAmount : 0;
        
        return {
            subtotal: subtotalBeforeIVA,
            iva: totalIVA,
            discount: totalDiscount,
            savingsVsDatabase: totalSavingsVsDatabase,
            subtotalWithIVA: subtotalWithIVA,
            total: subtotalWithIVA + percepcion + iibbDirecto - retencion,
            percepcion: percepcion,
            iibbDirecto: iibbDirecto,
            retencion: retencion
        };
    };

    // Get payment terms options based on payment method
    const getPaymentTermsOptions = () => {
        switch (selectedPaymentMethod) {
            case 'Efectivo':
            case 'Transferencia Bancaria':
                return [
                    { value: 'Pago por Adelantado', label: 'Pago por Adelantado' },
                    { value: 'Pago Contraentrega', label: 'Pago Contraentrega' },
                ];
            case 'Cheque':
                return [
                    { value: '30/60', label: '30/60 días' },
                    { value: '30/60/90', label: '30/60/90 días' },
                    { value: '30/60/90/120', label: '30/60/90/120 días' },
                ];
            case 'Cuenta Corriente':
                return [
                    { value: 'Pago Contraentrega', label: 'Pago Contraentrega' },
                    { value: '30 días', label: '30 días' },
                    { value: '60 días', label: '60 días' },
                    { value: '30/60', label: '30/60 días' },
                    { value: '30/60/90', label: '30/60/90 días' },
                    { value: 'A convenir', label: 'A convenir' },
                ];
            case 'Otros medios de pago electrónico':
                return [
                    { value: 'Pago Inmediato', label: 'Pago Inmediato' },
                    { value: '3 cuotas', label: '3 cuotas sin interés' },
                    { value: '6 cuotas', label: '6 cuotas sin interés' },
                    { value: '12 cuotas', label: '12 cuotas sin interés' },
                    { value: 'A convenir', label: 'A convenir' },
                ];
            default:
                return [];
        }
    };

    // Generate validation notes from checked products
    const generateValidationNotes = () => {
        const validatedProducts = orderItems.filter(item => item.checked && item.duplicateInfo);
        if (validatedProducts.length === 0) return '';

        const notes = validatedProducts.map(item => {
            const info = item.duplicateInfo;
            const effectivePrice = (item.bulk_price && item.bulk_price > 0) ? item.bulk_price : item.unit_price;
            const quantity = item.quantity || 1;
            
            if (info.status === 'unique') {
                return `[${item.product_code}] Producto único - Sin alternativas disponibles`;
            } else if (info.status === 'duplicate') {
                const lowestOption = info.duplicates?.find(d => d.is_lowest_cost);
                if (effectivePrice <= info.lowestCost) {
                    const savingsPercent = info.lowestCost > 0 ? ((info.lowestCost - effectivePrice) / info.lowestCost * 100) : 0;
                    return `[${item.product_code}] Mejor precio seleccionado (${savingsPercent.toFixed(1)}% mejor que ${info.count} marcas)`;
                } else {
                    const potentialSavingsUnit = effectivePrice - info.lowestCost;
                    const potentialSavingsTotal = potentialSavingsUnit * quantity;
                    return `[${item.product_code}] Alternativa: ${lowestOption?.brand_name} - ${lowestOption?.product_code} (Ahorro unitario: $${potentialSavingsUnit.toFixed(2)}, Ahorro total potencial: $${potentialSavingsTotal.toFixed(2)})`;
                }
            }
            return '';
        }).filter(note => note);

        return notes.join('\n');
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // ── Simplificado validation ──────────────────────────────────
            if (isSimplificado) {
                if (simpTotal <= 0) {
                    message.error('El Total debe ser mayor a cero');
                    return;
                }
                setLoading(true);

                const orderData = {
                    supplier_id: values.supplier_id,
                    supplier_razon_social: selectedSupplier?.razon_social || '',
                    supplier_cuit: selectedSupplier?.documento_nro || '',
                    order_date: values.order_date
                        ? dayjs(values.order_date).format('YYYY-MM-DD')
                        : dayjs().format('YYYY-MM-DD'),
                    expected_delivery_date: null,
                    payment_method: values.payment_method || '',
                    payment_terms: '',
                    payment_date_type: paymentDateType,
                    related_supplier_invoice: values.related_supplier_invoice || null,
                    notes: '',
                    cargas_impositivas_on: false,
                    retencion: 0,
                    percepcion: 0,
                    iibb_directo: 0,
                    is_simplified: true,
                    subtotal: parseFloat(simpSubtotal.toFixed(2)),
                    iva: parseFloat(simpIva.toFixed(2)),
                    discount: 0,
                    total: parseFloat(simpTotal.toFixed(2)),
                    items: []
                };

                const response = await createPedido(orderData);

                if (response.success) {
                    const createdOrder = response.data.order;

                    if (attachedFiles.length > 0) {
                        setUploadingFiles(true);
                        setFileUploadProgress({ current: 0, total: attachedFiles.length, percent: 0 });

                        let successCount = 0;
                        let failCount = 0;

                        for (let i = 0; i < attachedFiles.length; i++) {
                            const fileItem = attachedFiles[i];
                            const file = fileItem.originFileObj || fileItem;
                            try {
                                const validation = validateFile(file, i);
                                if (!validation.valid) { failCount++; continue; }
                                await uploadAttachment(createdOrder.id, file, (percent) => {
                                    setFileUploadProgress(prev => ({
                                        ...prev,
                                        percent: Math.round(((i + (percent / 100)) / attachedFiles.length) * 100)
                                    }));
                                });
                                successCount++;
                                setFileUploadProgress(prev => ({ ...prev, current: i + 1 }));
                            } catch (uploadError) {
                                console.error(`Error uploading file ${file.name}:`, uploadError);
                                failCount++;
                            }
                        }

                        setUploadingFiles(false);

                        if (successCount > 0 && failCount === 0) {
                            message.success(`Orden simplificada creada con ${successCount} archivo(s) adjunto(s)`);
                        } else if (successCount > 0 && failCount > 0) {
                            message.warning(`Orden creada. ${successCount} archivo(s) subido(s), ${failCount} fallido(s)`);
                        } else {
                            message.warning('Orden creada pero no se pudieron subir los archivos adjuntos');
                        }
                    } else {
                        message.success('Orden de compra simplificada creada exitosamente');
                    }

                    handleReset();
                    onOrderCreated(createdOrder);
                    onClose();
                }
                return;
            }

            // ── Completo validation ──────────────────────────────────────
            if (orderItems.length === 0) {
                message.error('Debe agregar al menos un producto a la orden');
                return;
            }

            setLoading(true);

            // TODO: Backend implementation pending for file upload
            // When backend is ready, create FormData and append files
            const hasFiles = attachedFiles.length > 0;
            
            if (hasFiles) {
                console.log('Files attached (waiting for backend):', attachedFiles.map(f => ({
                    name: f.name,
                    type: f.type,
                    size: f.size
                })));
                // TODO: Uncomment when backend is ready
                // const formData = new FormData();
                // formData.append('orderData', JSON.stringify(orderData));
                // attachedFiles.forEach((file) => {
                //     formData.append('attachments', file.originFileObj || file);
                // });
                // const response = await createPedido(formData);
            }

            // Calculate totals for order with 3-field pricing and discounts
            const orderTotals = calculateTotal();

            const itemsWithCalculations = orderItems.map(item => {
                // Step 1: Determine base price (Costo Compra or Costo Base de Datos)
                const basePrice = (item.bulk_price && item.bulk_price > 0) 
                    ? item.bulk_price 
                    : item.unit_price;
                
                // Step 2: Calculate discount
                const discountAmount = basePrice * (item.discount_percentage / 100);
                const netPrice = basePrice - discountAmount;
                const itemSubtotal = item.quantity * netPrice;
                const itemIva = itemSubtotal * (item.iva_percentage / 100);
                const itemTotal = itemSubtotal + itemIva;
                const totalDiscountAmount = discountAmount * item.quantity;

                return {
                    product_id: item.product_id || null,
                    product_code: item.product_code,
                    product_description: item.product_description || item.product_code,
                    brand: item.brand || '',
                    ean: item.ean || '',
                    quantity: item.quantity,
                    
                    // Pricing fields (3-field system)
                    unit_price: item.unit_price,           // Costo Base de Datos (DB reference)
                    bulk_price: item.bulk_price || 0,      // Costo Compra (supplier quote)
                    price_type: (item.bulk_price && item.bulk_price > 0) ? 'bulk' : 'unit',
                    
                    // Discount fields
                    discount_percentage: item.discount_percentage || 0,
                    discount_amount: parseFloat(totalDiscountAmount.toFixed(2)),
                    
                    // Calculated fields
                    subtotal: parseFloat(itemSubtotal.toFixed(2)),
                    iva_percentage: item.iva_percentage,
                    iva_amount: parseFloat(itemIva.toFixed(2)),
                    total: parseFloat(itemTotal.toFixed(2)),
                    
                    // Include duplicate check info if available
                    has_alternatives: item.duplicateInfo?.status === 'duplicate',
                    lowest_alternative_cost: item.duplicateInfo?.lowestCost || null,
                    potential_savings: item.duplicateInfo?.status === 'duplicate' 
                        ? (basePrice - (item.duplicateInfo?.lowestCost || 0)) * item.quantity
                        : null
                };
            });

            const orderData = {
                supplier_id: values.supplier_id,
                // Include supplier snapshot
                supplier_razon_social: selectedSupplier?.razon_social || '',
                supplier_cuit: selectedSupplier?.documento_nro || '',
                order_date: values.order_date
                    ? dayjs(values.order_date).format('YYYY-MM-DD')
                    : dayjs().format('YYYY-MM-DD'),
                expected_delivery_date: values.expected_delivery_date 
                    ? dayjs(values.expected_delivery_date).format('YYYY-MM-DD') 
                    : null,
                payment_terms: values.payment_terms || '',
                payment_method: values.payment_method || '',
                payment_date_type: paymentDateType,
                related_supplier_invoice: values.related_supplier_invoice || null,
                notes: values.notes || '',
                // Tax calculation flag
                cargas_impositivas_on: cargasImpositivasOn,
                // Include calculated totals with discount support
                subtotal: parseFloat(orderTotals.subtotal.toFixed(2)),
                iva: parseFloat(orderTotals.iva.toFixed(2)),
                discount: parseFloat(orderTotals.discount.toFixed(2)),
                // IIBB fields (new)
                retencion: parseFloat(orderTotals.retencion.toFixed(2)),
                percepcion: parseFloat(orderTotals.percepcion.toFixed(2)),
                iibb_directo: parseFloat(orderTotals.iibbDirecto.toFixed(2)),
                // Total now includes all taxes
                total: parseFloat(orderTotals.total.toFixed(2)),
                is_simplified: false,
                // Include custom payment date if 'A convenir' is selected
                ...(selectedPaymentTerms === 'A convenir' && customPaymentDate ? {
                    custom_payment_date: dayjs(customPaymentDate).format('YYYY-MM-DD')
                } : {}),
                items: itemsWithCalculations
            };

            const response = await createPedido(orderData);

            if (response.success) {
                const createdOrder = response.data.order;
                
                // Upload attachments if any files were selected
                if (attachedFiles.length > 0) {
                    setUploadingFiles(true);
                    setFileUploadProgress({ current: 0, total: attachedFiles.length, percent: 0 });
                    
                    let successCount = 0;
                    let failCount = 0;
                    
                    for (let i = 0; i < attachedFiles.length; i++) {
                        const fileItem = attachedFiles[i];
                        const file = fileItem.originFileObj || fileItem;
                        
                        try {
                            // Validate file before upload
                            const validation = validateFile(file, i);
                            if (!validation.valid) {
                                console.warn(`Skipping file ${file.name}: ${validation.error}`);
                                failCount++;
                                continue;
                            }
                            
                            await uploadAttachment(createdOrder.id, file, (percent) => {
                                setFileUploadProgress(prev => ({
                                    ...prev,
                                    percent: Math.round(((i + (percent / 100)) / attachedFiles.length) * 100)
                                }));
                            });
                            successCount++;
                            setFileUploadProgress(prev => ({
                                ...prev,
                                current: i + 1
                            }));
                        } catch (uploadError) {
                            console.error(`Error uploading file ${file.name}:`, uploadError);
                            failCount++;
                        }
                    }
                    
                    setUploadingFiles(false);
                    
                    // Show summary message
                    if (successCount > 0 && failCount === 0) {
                        message.success(`Orden creada con ${successCount} archivo(s) adjunto(s)`);
                    } else if (successCount > 0 && failCount > 0) {
                        message.warning(`Orden creada. ${successCount} archivo(s) subido(s), ${failCount} fallido(s)`);
                    } else if (failCount > 0) {
                        message.warning('Orden creada pero no se pudieron subir los archivos adjuntos');
                    }
                } else {
                    message.success('Orden de compra creada exitosamente');
                }
                
                handleReset();
                onOrderCreated(createdOrder);
                onClose();
            }
        } catch (error) {
            console.error('Error creating order:', error);
            
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                message.error(errorData.message || 'Error al crear orden de compra');
            } else {
                message.error('Error al crear orden de compra');
            }
        } finally {
            setLoading(false);
        }
    };

    // Reset form and items
    const handleReset = () => {
        form.resetFields();
        setOrderItems([]);
        setSelectedSupplier(null);
        setSearchProduct('');
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedPaymentMethod(null);
        setSelectedPaymentTerms(null);
        setCustomPaymentDate(null);
        setAttachedFiles([]);
        setPreviewFile(null);
        setFilePreviewVisible(false);
        setUploadingFiles(false);
        setFileUploadProgress({ current: 0, total: 0, percent: 0 });
        // Reset tax state
        setTaxInputMode('amount');
        setRetencionAmount(0);
        setPercepcionAmount(0);
        setIibbDirectoAmount(0);
        setRetencionPct(0);
        setPercepcionPct(0);
        setIibbDirectoPct(0);
        setCargasImpositivasOn(true);
        // Reset simplificado mode totals (keep isSimplificado as-is — user chose it consciously)
        setSimpSubtotal(0);
        setSimpIva(0);
        setSimpTotal(0);
        setPaymentDateType('A_DETERMINAR');
        // Reset custom product modal
        setCustomProductModalVisible(false);
        customProductForm.resetFields();
    };

    // Handle modal close
    const handleCancel = () => {
        handleReset();
        onClose();
    };

    // Table columns for order items
    const itemColumns = [
        {
            title: 'Código',
            dataIndex: 'product_code',
            key: 'product_code',
            width: 140,
            render: (text, record) => (
                <Space size="small">
                    <span>{text}</span>
                    {isCustomProduct(record) && (
                        <Badge 
                            count="CUSTOM" 
                            style={{ 
                                backgroundColor: '#722ed1',
                                fontSize: '10px',
                                padding: '0 4px'
                            }} 
                        />
                    )}
                </Space>
            ),
        },
        {
            title: 'Marca',
            dataIndex: 'brand',
            key: 'brand',
            width: 100,
            render: (text) => text || '-',
        },
        {
            title: 'Descripción',
            dataIndex: 'product_description',
            key: 'product_description',
            render: (text, record) => {
                const duplicateInfo = record.duplicateInfo;
                
                if (duplicateInfo?.status === 'duplicate' && duplicateInfo.duplicates?.length > 0) {
                    // Find the lowest cost option
                    const lowestOption = duplicateInfo.duplicates.find(d => d.is_lowest_cost);
                    
                    // Get current user-entered price (prioritize bulk_price if available)
                    const currentPrice = (record.bulk_price && record.bulk_price > 0) 
                        ? record.bulk_price 
                        : (record.unit_price || 0);
                    const lowestPrice = duplicateInfo.lowestCost || 0;
                    
                    let alertMessage;
                    let alertType = 'warning';
                    
                    // Scenario 1: User hasn't entered a price yet (or price is 0)
                    if (currentPrice === 0) {
                        alertMessage = (
                            <span style={{ fontSize: '11px' }}>
                                ℹ️ Verificar precio contra {duplicateInfo.count} marcas disponibles.
                            </span>
                        );
                        alertType = 'info';
                    }
                    // Scenario 2: User's price is equal or better than lowest DB cost
                    else if (currentPrice <= lowestPrice) {
                        const savingsAmount = lowestPrice - currentPrice;
                        const savingsPercent = lowestPrice > 0 ? (savingsAmount / lowestPrice * 100) : 0;
                        
                        // If savings is essentially 0 (less than 0.1%), show simpler message
                        if (savingsPercent < 0.1) {
                            alertMessage = (
                                <span style={{ fontSize: '11px' }}>
                                    ✅ ¡Excelente precio! Mejor de las {duplicateInfo.count} marcas en stock.
                                </span>
                            );
                        } else {
                            alertMessage = (
                                <span style={{ fontSize: '11px' }}>
                                    ✅ ¡Excelente precio! {savingsPercent.toFixed(1)}% mejor que las {duplicateInfo.count} marcas en stock.
                                </span>
                            );
                        }
                        alertType = 'success';
                    }
                    // Scenario 3: User's price is higher - show potential savings
                    else {
                        const savingsAmount = currentPrice - lowestPrice;
                        const savingsPercent = (savingsAmount / currentPrice * 100);
                        alertMessage = (
                            <span style={{ fontSize: '11px' }}>
                                Encontrado en {duplicateInfo.count} marcas. 
                                <strong> {lowestOption?.brand_name}</strong> - <strong>{lowestOption?.product_code}</strong>: 
                                ahorro unitario potencial {savingsPercent.toFixed(1)}% (${savingsAmount.toFixed(2)})
                            </span>
                        );
                        alertType = 'warning';
                    }
                    
                    return (
                        <div>
                            <Text>{text || 'Sin descripción'}</Text>
                            <div style={{ marginTop: 4 }}>
                                <Alert
                                    message={alertMessage}
                                    type={alertType}
                                    showIcon
                                    style={{ padding: '2px 8px', fontSize: '11px' }}
                                />
                            </div>
                        </div>
                    );
                }
                
                return <Text>{text || 'Sin descripción'}</Text>;
            },
        },
        {
            title: 'Cantidad',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 100,
            render: (text, record) => (
                <InputNumber
                    value={text}
                    onChange={(value) => handleItemChange(record.key, 'quantity', value)}
                    min={1}
                    size="small"
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            title: 'Costo Base de Datos',
            dataIndex: 'unit_price',
            key: 'unit_price',
            width: 130,
            render: (text, record) => (
                <InputNumber
                    value={text}
                    disabled={true}  // Read-only: database reference price
                    min={0}
                    step={0.01}
                    precision={2}
                    size="small"
                    style={{ 
                        width: '100%',
                        backgroundColor: '#f5f5f5'  // Visual indicator for read-only
                    }}
                    prefix="$"
                />
            ),
        },
        {
            title: 'Costo Compra',
            dataIndex: 'bulk_price',
            key: 'bulk_price',
            width: 120,
            render: (text, record) => (
                <InputNumber
                    value={text}
                    onChange={(value) => handleItemChange(record.key, 'bulk_price', value)}
                    min={0}
                    step={0.01}
                    precision={2}
                    size="small"
                    style={{ width: '100%' }}
                    prefix="$"
                    placeholder="Precio compra"
                />
            ),
        },
        {
            title: 'Descuento %',
            dataIndex: 'discount_percentage',
            key: 'discount_percentage',
            width: 110,
            render: (text, record) => {
                const hasCustomDiscount = record.has_custom_discount;
                
                // Calculate base price (Costo Compra or Costo Base de Datos)
                const basePrice = (record.bulk_price && record.bulk_price > 0) 
                    ? record.bulk_price 
                    : record.unit_price;
                
                const discountAmount = (basePrice * (text / 100)).toFixed(2);
                const savingsVsDatabase = record.unit_price - (basePrice - parseFloat(discountAmount));
                
                return (
                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <InputNumber
                            value={text}
                            onChange={(value) => handleDiscountChange(record.key, value)}
                            min={0}
                            max={99.99}
                            step={0.01}
                            precision={2}
                            size="small"
                            style={{ 
                                width: '100%',
                                backgroundColor: hasCustomDiscount ? '#fffbe6' : 'white'
                            }}
                            suffix="%"
                        />
                        {text > 0 && (
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                -${discountAmount}
                            </Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'IVA %',
            dataIndex: 'iva_percentage',
            key: 'iva_percentage',
            width: 80,
            render: (text, record) => (
                <InputNumber
                    value={text}
                    onChange={(value) => handleItemChange(record.key, 'iva_percentage', value)}
                    min={0}
                    max={100}
                    step={0.5}
                    size="small"
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => {
                const hasDuplicates = record.duplicateInfo?.status === 'duplicate';
                const isUnique = record.duplicateInfo?.status === 'unique';
                
                return (
                    <Space size="small">
                        <Button
                            size="small"
                            icon={<CheckOutlined />}
                            type={record.checked ? 'primary' : 'default'}
                            onClick={() => handleCheckItem(record.key)}
                            title="Chequear producto"
                            loading={checkingDuplicates[record.key]}
                            style={{
                                ...(isUnique ? { backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' } : {}),
                                ...(hasDuplicates ? { backgroundColor: '#faad14', borderColor: '#faad14', color: 'white' } : {})
                            }}
                        />
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveItem(record.key)}
                            title="Quitar producto"
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <Modal
            title="Nueva Orden de Compra"
            open={visible}
            onCancel={handleCancel}
            width={1400}
            style={{ top: 20 }}
            styles={{ body: { paddingTop: 8, paddingBottom: 8 } }}
            footer={[
                <Button key="reset" onClick={handleReset} danger disabled={loading || uploadingFiles}>
                    Borrar Pedido
                </Button>,
                <Button key="cancel" onClick={handleCancel} disabled={loading || uploadingFiles}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading || uploadingFiles}
                    onClick={handleSubmit}
                    disabled={isSimplificado
                        ? !selectedSupplier || simpTotal <= 0
                        : !selectedSupplier || orderItems.length === 0}
                    icon={uploadingFiles ? <LoadingOutlined /> : null}
                >
                    {uploadingFiles 
                        ? `Subiendo archivos (${fileUploadProgress.current}/${fileUploadProgress.total})...` 
                        : 'Cargar Pedido'}
                </Button>
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                size="small"
            >
                {/* Mode selector */}
                <div style={{
                    background: isSimplificado
                        ? 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)'
                        : 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
                    border: `2px solid ${isSimplificado ? '#b37feb' : '#91caff'}`,
                    borderRadius: 10,
                    padding: '10px 12px 8px',
                    marginBottom: 14,
                    transition: 'all 0.3s ease',
                }}>
                    <div style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: isSimplificado ? '#9254de' : '#1677ff',
                        marginBottom: 6,
                        userSelect: 'none',
                        transition: 'color 0.3s ease',
                    }}>
                        Tipo de Orden
                    </div>
                    <Segmented
                        options={[
                            {
                                label: (
                                    <span style={{
                                        display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0',
                                        color: isSimplificado ? '#722ed1' : '#595959',
                                        fontWeight: isSimplificado ? 700 : 400,
                                        fontSize: 13,
                                    }}>
                                        <span style={{ fontSize: 15 }}>⚡</span> Simplificada
                                    </span>
                                ),
                                value: 'Simplificada',
                            },
                            {
                                label: (
                                    <span style={{
                                        display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0',
                                        color: isSimplificado ? '#595959' : '#1677ff',
                                        fontWeight: isSimplificado ? 400 : 700,
                                        fontSize: 13,
                                    }}>
                                        <span style={{ fontSize: 15 }}>📋</span> Completa
                                    </span>
                                ),
                                value: 'Completa',
                            },
                        ]}
                        value={isSimplificado ? 'Simplificada' : 'Completa'}
                        onChange={(val) => {
                            const goSimp = val === 'Simplificada';
                            setIsSimplificado(goSimp);
                            if (goSimp) {
                                setOrderItems([]);
                                setSearchResults([]);
                                setSearchProduct('');
                                setShowSearchResults(false);
                            } else {
                                setSimpSubtotal(0);
                                setSimpIva(0);
                                setSimpTotal(0);
                            }
                        }}
                        block
                        style={{ background: '#e0e0e0' }}
                    />
                    <div style={{
                        fontSize: 11,
                        color: isSimplificado ? '#9254de' : '#4096ff',
                        marginTop: 6,
                        textAlign: 'center',
                        transition: 'color 0.3s ease',
                    }}>
                        {isSimplificado
                            ? 'Ingresá los totales directamente desde la factura del proveedor'
                            : 'Cargá los productos uno a uno con precios y cantidades'}
                    </div>
                </div>

                <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>Seleccionar Proveedor</Divider>

                <Form.Item
                    name="supplier_id"
                    label="Proveedor"
                    rules={[{ required: true, message: 'Por favor seleccione un proveedor' }]}
                    style={{ marginBottom: 6 }}
                >
                    <Select
                        placeholder="Seleccione un proveedor"
                        loading={loadingSuppliers}
                        showSearch
                        filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={handleSupplierChange}
                    >
                        {suppliers.map(supplier => (
                            <Option key={supplier.id} value={supplier.id}>
                                {supplier.razon_social} - {supplier.documento_nro}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {selectedSupplier && (
                    <>
                        {/* ═══════════════════════════════════════════════ */}
                        {/* MODO SIMPLIFICADO                               */}
                        {/* ═══════════════════════════════════════════════ */}
                        {isSimplificado && (
                            <>
                                <Divider orientation="left" style={{ margin: '2px 0 10px 0' }}>Datos de la Factura</Divider>

                                <Form.Item
                                    name="related_supplier_invoice"
                                    label="Factura del Proveedor"
                                    rules={[{ required: true, message: 'Ingrese el número de factura del proveedor' }]}
                                    style={{ marginBottom: 10 }}
                                >
                                    <Input
                                        placeholder="Ej: FC-A-0007-00048350"
                                        maxLength={50}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="order_date"
                                    label="Fecha Orden"
                                    initialValue={dayjs()}
                                    style={{ marginBottom: 10 }}
                                >
                                    <DatePicker
                                        locale={locale}
                                        format="DD/MM/YYYY"
                                        placeholder="Fecha de la orden / factura"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="payment_method"
                                    label="Método de Pago"
                                    style={{ marginBottom: 10 }}
                                >
                                    <Select
                                        placeholder="Seleccione método de pago"
                                        allowClear
                                        onChange={(value) => {
                                            setSelectedPaymentMethod(value);
                                            form.setFieldsValue({ payment_terms: null });
                                        }}
                                    >
                                        <Option value="Efectivo">Efectivo</Option>
                                        <Option value="Transferencia Bancaria">Transferencia Bancaria</Option>
                                        <Option value="Cheque">Cheque</Option>
                                        <Option value="Cuenta Corriente">Cuenta Corriente</Option>
                                        <Option value="Otros medios de pago electrónico">Otros medios de pago electrónico</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item label="Momento de Pago" style={{ marginBottom: 10 }}>
                                    <Segmented
                                        value={paymentDateType}
                                        onChange={setPaymentDateType}
                                        options={[
                                            { label: '⚡ Adelantado', value: 'ADELANTADO' },
                                            { label: '🚚 Contraentrega', value: 'CONTRAENTREGA' },
                                            { label: '📅 A Determinar', value: 'A_DETERMINAR' },
                                        ]}
                                        block
                                    />
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                                        {paymentDateType === 'ADELANTADO' && 'Fecha de pago: hoy (al crear la orden)'}
                                        {paymentDateType === 'CONTRAENTREGA' && 'Fecha de pago: al momento de la entrega'}
                                        {paymentDateType === 'A_DETERMINAR' && 'Fecha de pago: a confirmar'}
                                    </div>
                                </Form.Item>

                                <Divider orientation="left" style={{ margin: '2px 0 10px 0' }}>Montos de la Factura</Divider>

                                <Row gutter={16} style={{ marginBottom: 10 }}>
                                    <Col span={8}>
                                        <Form.Item label="Subtotal (sin IVA)" style={{ marginBottom: 0 }}>
                                            <InputNumber
                                                value={simpSubtotal}
                                                onChange={(v) => {
                                                    const val = v || 0;
                                                    setSimpSubtotal(val);
                                                    if (simpIva > 0) setSimpTotal(parseFloat((val + simpIva).toFixed(2)));
                                                    else if (simpTotal > 0) setSimpIva(parseFloat((simpTotal - val).toFixed(2)));
                                                }}
                                                min={0}
                                                precision={2}
                                                style={{ width: '100%' }}
                                                addonBefore="$"
                                                placeholder="0.00"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label="IVA inscripto" style={{ marginBottom: 0 }}>
                                            <InputNumber
                                                value={simpIva}
                                                onChange={(v) => {
                                                    const val = v || 0;
                                                    setSimpIva(val);
                                                    if (simpSubtotal > 0) setSimpTotal(parseFloat((simpSubtotal + val).toFixed(2)));
                                                    else if (simpTotal > 0) setSimpSubtotal(parseFloat((simpTotal - val).toFixed(2)));
                                                }}
                                                min={0}
                                                precision={2}
                                                style={{ width: '100%' }}
                                                addonBefore="$"
                                                placeholder="0.00"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label={<strong>Total</strong>} style={{ marginBottom: 0 }}>
                                            <InputNumber
                                                value={simpTotal}
                                                onChange={(v) => {
                                                    const val = v || 0;
                                                    setSimpTotal(val);
                                                    if (simpSubtotal > 0) setSimpIva(parseFloat((val - simpSubtotal).toFixed(2)));
                                                    else if (simpIva > 0) setSimpSubtotal(parseFloat((val - simpIva).toFixed(2)));
                                                }}
                                                min={0}
                                                precision={2}
                                                style={{ width: '100%', fontWeight: 'bold' }}
                                                addonBefore="$"
                                                placeholder="0.00"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/* Reference info: subtotal + IVA may not equal total (mixed IVA rates) */}
                                {(simpSubtotal > 0 || simpIva > 0) && (
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 12 }}
                                        message={
                                            <span style={{ fontSize: '12px' }}>
                                                Subtotal + IVA = <strong>${(simpSubtotal + simpIva).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                                                {Math.abs((simpSubtotal + simpIva) - simpTotal) > 0.01 && simpTotal > 0 && (
                                                    <span style={{ color: '#8c8c8c' }}>
                                                        {' '}· El Total difiere — normal en facturas con alícuotas mixtas (0% / 10,5% / 21%).
                                                    </span>
                                                )}
                                            </span>
                                        }
                                    />
                                )}
                            </>
                        )}

                        {/* ═══════════════════════════════════════════════ */}
                        {/* MODO COMPLETO                                   */}
                        {/* ═══════════════════════════════════════════════ */}
                        {!isSimplificado && (
                            <>
                        {/* Tax Charges Toggle */}
                        <div style={{ 
                            marginBottom: 12, 
                            padding: '8px 12px', 
                            backgroundColor: cargasImpositivasOn ? '#f6ffed' : '#fff7e6',
                            borderRadius: 6,
                            border: `1px solid ${cargasImpositivasOn ? '#b7eb8f' : '#ffd591'}`
                        }}>
                            <Row align="middle" justify="space-between">
                                <Col>
                                    <Space>
                                        <Text strong style={{ fontSize: '13px' }}>
                                            {cargasImpositivasOn ? '📊 Con cargas impositivas' : '⚠️ Sin cargas impositivas'}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                            {cargasImpositivasOn 
                                                ? '(IVA, Retención, Percepción, IIBB activos)' 
                                                : '(Proveedor no declara impuestos)'}
                                        </Text>
                                    </Space>
                                </Col>
                                <Col>
                                    <Switch
                                        checked={cargasImpositivasOn}
                                        onChange={(checked) => setCargasImpositivasOn(checked)}
                                        checkedChildren="CON"
                                        unCheckedChildren="SIN"
                                    />
                                </Col>
                            </Row>
                        </div>

                        {/* Montos Impositivos Section - only show if cargas impositivas is ON */}
                        {cargasImpositivasOn && (
                            <div style={{ 
                                marginBottom: 14, 
                                padding: '16px 20px', 
                                backgroundColor: '#fafafa',
                                borderRadius: 6,
                                border: '1px solid #d9d9d9'
                            }}>
                                <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                                    <Col>
                                        <Text strong style={{ fontSize: '16px' }}>
                                            📋 Montos Impositivos
                                        </Text>
                                    </Col>
                                    <Col>
                                        <Space size="small">
                                            <Text type="secondary" style={{ fontSize: '14px' }}>
                                                Modo:
                                            </Text>
                                            <Button.Group>
                                                <Button 
                                                    type={taxInputMode === 'amount' ? 'primary' : 'default'}
                                                    onClick={() => setTaxInputMode('amount')}
                                                >
                                                    $ Monto
                                                </Button>
                                                <Button 
                                                    type={taxInputMode === 'percentage' ? 'primary' : 'default'}
                                                    onClick={() => setTaxInputMode('percentage')}
                                                >
                                                    % Porcentaje
                                                </Button>
                                            </Button.Group>
                                        </Space>
                                    </Col>
                                </Row>
                                
                                <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: 14 }}>
                                    {taxInputMode === 'amount' 
                                        ? '💡 Ingrese los montos exactos de la factura del proveedor'
                                        : `💡 Base de cálculo: $${getTaxBase().toLocaleString('es-AR', { minimumFractionDigits: 2 })} (subtotal neto)`
                                    }
                                </Text>
                                
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: 6, color: '#52c41a' }}>
                                            Retención IIBB
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#8c8c8c', marginBottom: 8 }}>
                                            (Ud. retiene al proveedor)
                                        </div>
                                        <InputNumber
                                            min={0}
                                            precision={2}
                                            value={taxInputMode === 'amount' ? retencionAmount : retencionPct}
                                            onChange={handleRetencionChange}
                                            style={{ width: '100%' }}
                                            placeholder="0.00"
                                            addonBefore={taxInputMode === 'amount' ? '$' : null}
                                            addonAfter={taxInputMode === 'percentage' ? '%' : null}
                                        />
                                        <div style={{ fontSize: '13px', color: '#1890ff', marginTop: 6 }}>
                                            {taxInputMode === 'amount' 
                                                ? (getTaxBase() > 0 
                                                    ? `→ ${(retencionAmount / getTaxBase() * 100).toFixed(2)}%`
                                                    : (retencionAmount > 0 ? '→ Agregue productos para ver %' : '→ 0.00%'))
                                                : `→ $${retencionAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                                            }
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: 6, color: '#d46b08' }}>
                                            PERC.II.BB. (Percepción)
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#8c8c8c', marginBottom: 8 }}>
                                            (El proveedor le cobra)
                                        </div>
                                        <InputNumber
                                            min={0}
                                            precision={2}
                                            value={taxInputMode === 'amount' ? percepcionAmount : percepcionPct}
                                            onChange={handlePercepcionChange}
                                            style={{ width: '100%' }}
                                            placeholder="0.00"
                                            addonBefore={taxInputMode === 'amount' ? '$' : null}
                                            addonAfter={taxInputMode === 'percentage' ? '%' : null}
                                        />
                                        <div style={{ fontSize: '13px', color: '#1890ff', marginTop: 6 }}>
                                            {taxInputMode === 'amount' 
                                                ? (getTaxBase() > 0 
                                                    ? `→ ${(percepcionAmount / getTaxBase() * 100).toFixed(2)}%`
                                                    : (percepcionAmount > 0 ? '→ Agregue productos para ver %' : '→ 0.00%'))
                                                : `→ $${percepcionAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                                            }
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: 6, color: '#722ed1' }}>
                                            IIBB Directo Provincial
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#8c8c8c', marginBottom: 8 }}>
                                            (Impuesto provincial)
                                        </div>
                                        <InputNumber
                                            min={0}
                                            precision={2}
                                            value={taxInputMode === 'amount' ? iibbDirectoAmount : iibbDirectoPct}
                                            onChange={handleIibbDirectoChange}
                                            style={{ width: '100%' }}
                                            placeholder="0.00"
                                            addonBefore={taxInputMode === 'amount' ? '$' : null}
                                            addonAfter={taxInputMode === 'percentage' ? '%' : null}
                                        />
                                        <div style={{ fontSize: '13px', color: '#1890ff', marginTop: 6 }}>
                                            {taxInputMode === 'amount' 
                                                ? (getTaxBase() > 0 
                                                    ? `→ ${(iibbDirectoAmount / getTaxBase() * 100).toFixed(2)}%`
                                                    : (iibbDirectoAmount > 0 ? '→ Agregue productos para ver %' : '→ 0.00%'))
                                                : `→ $${iibbDirectoAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                                            }
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                        
                        <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>Agregar Productos</Divider>

                        <Row gutter={8} style={{ marginBottom: 8 }}>
                            <Col span={15}>
                                <Input
                                    placeholder="Buscar producto por código o descripción..."
                                    value={searchProduct}
                                    onChange={(e) => setSearchProduct(e.target.value)}
                                    onPressEnter={handleProductSearch}
                                    prefix={<SearchOutlined />}
                                    allowClear
                                    disabled={searchingProducts}
                                />
                            </Col>
                            <Col span={4}>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleProductSearch}
                                    loading={searchingProducts}
                                    block
                                >
                                    Buscar
                                </Button>
                            </Col>
                            <Col span={5}>
                                <Button
                                    icon={<AppstoreAddOutlined />}
                                    onClick={() => setCustomProductModalVisible(true)}
                                    block
                                    style={{ 
                                        backgroundColor: '#722ed1',
                                        borderColor: '#722ed1',
                                        color: 'white'
                                    }}
                                >
                                    + Personalizado
                                </Button>
                            </Col>
                        </Row>

                        {showSearchResults && (
                            <Card 
                                size="small" 
                                style={{ marginBottom: 8, maxHeight: 300, overflowY: 'auto' }}
                                title={
                                    <span style={{ fontSize: '12px' }}>
                                        {searchResults.length > 0 
                                            ? `${searchResults.length} producto(s) encontrado(s)` 
                                            : 'No se encontraron productos'}
                                    </span>
                                }
                                extra={
                                    <Button 
                                        size="small" 
                                        type="text" 
                                        onClick={() => setShowSearchResults(false)}
                                    >
                                        Cerrar
                                    </Button>
                                }
                            >
                                {searchResults.length > 0 ? (
                                    <List
                                        size="small"
                                        dataSource={searchResults}
                                        renderItem={(product) => (
                                            <List.Item
                                                style={{ cursor: 'pointer', padding: '8px 0' }}
                                                onClick={() => handleSelectProduct(product)}
                                                actions={[
                                                    <Button 
                                                        type="primary" 
                                                        size="small" 
                                                        icon={<PlusOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectProduct(product);
                                                        }}
                                                    >
                                                        Agregar
                                                    </Button>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={
                                                        <Space>
                                                            <strong>{product.ProductCode}</strong>
                                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                                {product.Brand?.BrandName}
                                                            </span>
                                                            {product.MatchedByBrand && (
                                                                <Badge count="Marca" style={{ backgroundColor: '#52c41a' }} />
                                                            )}
                                                            {product.MatchedByCategory && (
                                                                <Badge count="Categoría" style={{ backgroundColor: '#1890ff' }} />
                                                            )}
                                                        </Space>
                                                    }
                                                    description={
                                                        <div>
                                                            <div>{product.ProductDescription}</div>
                                                            <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                                                                {product.EAN && `EAN: ${product.EAN} | `}
                                                                Costo: ${product.ProductCost?.toFixed(2) || '0.00'}
                                                                {product.Brand?.CategoryName && ` | ${product.Brand.CategoryName}`}
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                ) : (
                                    <Empty 
                                        description="No se encontraron productos que coincidan con tu búsqueda" 
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                )}
                            </Card>
                        )}

                        <Table
                            columns={itemColumns}
                            dataSource={orderItems}
                            pagination={false}
                            size="small"
                            scroll={{ x: 1100 }}
                            style={{ marginBottom: 8 }}
                            locale={{ emptyText: 'No hay productos agregados' }}
                        />


                        {orderItems.length > 0 && (() => {
                            const orderTotals = calculateTotal();
                            const { subtotal, iva, discount, savingsVsDatabase, subtotalWithIVA, total, percepcion, iibbDirecto, retencion } = orderTotals;
                            
                            return (
                                <div style={{ textAlign: 'right', marginBottom: 8 }}>
                                    {discount > 0 && (
                                        <div style={{ fontSize: '13px', color: '#cf1322' }}>
                                            Descuento: -${discount.toFixed(2)}
                                        </div>
                                    )}
                                    {savingsVsDatabase > 0 && (
                                        <div style={{ fontSize: '12px', color: '#52c41a' }}>
                                            💰 Ahorro vs. BD: -${savingsVsDatabase.toFixed(2)}
                                        </div>
                                    )}
                                    <div style={{ fontSize: '13px', color: '#595959' }}>
                                        Subtotal: ${subtotal.toFixed(2)}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#595959' }}>
                                        IVA: ${iva.toFixed(2)}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#595959', marginBottom: 4 }}>
                                        Subtotal + IVA: ${subtotalWithIVA.toFixed(2)}
                                    </div>
                                    {percepcion > 0 && (
                                        <div style={{ fontSize: '13px', color: '#d46b08' }}>
                                            + PERC.II.BB.: ${percepcion.toFixed(2)}
                                        </div>
                                    )}
                                    {iibbDirecto > 0 && (
                                        <div style={{ fontSize: '13px', color: '#722ed1' }}>
                                            + IIBB Directo: ${iibbDirecto.toFixed(2)}
                                        </div>
                                    )}
                                    {retencion > 0 && (
                                        <div style={{ fontSize: '13px', color: '#52c41a' }}>
                                            - Retención: ${retencion.toFixed(2)}
                                        </div>
                                    )}
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginTop: 4 }}>
                                        Total Estimado: ${total.toFixed(2)}
                                    </div>
                                </div>
                            );
                        })()}

                        <Divider orientation="left" style={{ margin: '12px 0 12px 0' }}>Descuento Global</Divider>

                        <Form.Item style={{ marginBottom: 16 }}>
                            <Row gutter={16} align="middle">
                                <Col span={6}>
                                    <Text strong>Descuento Global (%):</Text>
                                </Col>
                                <Col span={6}>
                                    <InputNumber
                                        value={globalDiscount}
                                        onChange={handleGlobalDiscountChange}
                                        min={0}
                                        max={99.99}
                                        step={0.01}
                                        precision={2}
                                        style={{ width: '100%' }}
                                        suffix="%"
                                        placeholder="0.00"
                                    />
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Aplica a todos los productos. Los descuentos individuales lo reemplazan.
                                    </Text>
                                </Col>
                            </Row>
                        </Form.Item>

                        <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>Detalles del Pedido</Divider>

                        <Form.Item label="Momento de Pago" style={{ marginBottom: 10 }}>
                            <Segmented
                                value={paymentDateType}
                                onChange={setPaymentDateType}
                                options={[
                                    { label: '⚡ Adelantado', value: 'ADELANTADO' },
                                    { label: '🚚 Contraentrega', value: 'CONTRAENTREGA' },
                                    { label: '📅 A Determinar', value: 'A_DETERMINAR' },
                                ]}
                                block
                            />
                            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                                {paymentDateType === 'ADELANTADO' && 'Fecha de pago: hoy (al crear la orden)'}
                                {paymentDateType === 'CONTRAENTREGA' && 'Fecha de pago: fecha de entrega esperada'}
                                {paymentDateType === 'A_DETERMINAR' && 'Fecha de pago: a confirmar'}
                            </div>
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="payment_method"
                                    label="Método de Pago"
                                    style={{ marginBottom: 6 }}
                                >
                                    <Select 
                                        placeholder="Seleccione método de pago"
                                        onChange={(value) => {
                                            setSelectedPaymentMethod(value);
                                            // Clear payment terms when method changes
                                            form.setFieldsValue({ payment_terms: null });
                                        }}
                                    >
                                        <Option value="Efectivo">Efectivo</Option>
                                        <Option value="Transferencia Bancaria">Transferencia Bancaria</Option>
                                        <Option value="Cheque">Cheque</Option>
                                        <Option value="Cuenta Corriente">Cuenta Corriente</Option>
                                        <Option value="Otros medios de pago electrónico">Otros medios de pago electrónico</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="payment_terms"
                                    label="Condiciones de Pago"
                                    style={{ marginBottom: 6 }}
                                >
                                    <Select 
                                        placeholder={selectedPaymentMethod ? "Seleccione condiciones" : "Primero seleccione método de pago"}
                                        disabled={!selectedPaymentMethod}
                                        onChange={(value) => {
                                            setSelectedPaymentTerms(value);
                                            if (value !== 'A convenir') {
                                                setCustomPaymentDate(null);
                                            }
                                        }}
                                    >
                                        {getPaymentTermsOptions().map(option => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                {selectedPaymentTerms === 'A convenir' && (
                                    <Form.Item
                                        label="Fecha de Pago Acordada"
                                        style={{ marginBottom: 6, marginTop: -6 }}
                                    >
                                        <DatePicker
                                            locale={locale}
                                            format="DD/MM/YYYY"
                                            placeholder="Seleccione fecha de pago"
                                            style={{ width: '100%' }}
                                            value={customPaymentDate}
                                            onChange={setCustomPaymentDate}
                                            disabledDate={(current) => {
                                                // Disable dates before today
                                                return current && current < dayjs().startOf('day');
                                            }}
                                        />
                                    </Form.Item>
                                )}
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="order_date"
                                    label="Fecha Orden"
                                    initialValue={dayjs()}
                                    style={{ marginBottom: 6 }}
                                >
                                    <DatePicker
                                        locale={locale}
                                        format="DD/MM/YYYY"
                                        placeholder="Fecha de la orden / factura"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="expected_delivery_date"
                                    label="Fecha Esperada"
                                    style={{ marginBottom: 6 }}
                                >
                                    <DatePicker
                                        locale={locale}
                                        format="DD/MM/YYYY"
                                        placeholder="Seleccione fecha"
                                        style={{ width: '100%' }}
                                        disabledDate={(current) => {
                                            // Disable dates before today
                                            return current && current < dayjs().startOf('day');
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="related_supplier_invoice"
                            label="Factura del Proveedor Relacionada"
                            style={{ marginBottom: 6 }}
                        >
                            <Input
                                placeholder="Ej: FC-A-0007-00048350"
                                maxLength={50}
                            />
                        </Form.Item>

                        <Form.Item
                            name="notes"
                            label="Notas"
                            style={{ marginBottom: 6 }}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Notas adicionales para la orden..."
                            />
                        </Form.Item>
                        {generateValidationNotes() && (
                            <div style={{ 
                                marginTop: -6, 
                                marginBottom: 6, 
                                padding: '8px 12px',
                                backgroundColor: '#f6ffed',
                                border: '1px solid #b7eb8f',
                                borderRadius: '4px',
                                fontSize: '11px', 
                                color: '#52c41a' 
                            }}>
                                <strong>📋 Notas de validación de precios:</strong>
                                <pre style={{ margin: '4px 0 0 0', fontSize: '11px', whiteSpace: 'pre-wrap', color: '#333' }}>
                                    {generateValidationNotes()}
                                </pre>
                            </div>
                        )}

                            </>
                        ) /* end !isSimplificado */}

                        <Divider orientation="left" style={{ margin: '8px 0 6px 0' }}>
                            Archivos Adjuntos (Opcional)
                        </Divider>

                        {uploadingFiles && (
                            <Alert
                                message={`Subiendo archivos (${fileUploadProgress.current}/${fileUploadProgress.total})`}
                                description={
                                    <div style={{ marginTop: 8 }}>
                                        <div className="progress-bar" style={{ 
                                            width: '100%', 
                                            height: '8px', 
                                            background: '#f0f0f0', 
                                            borderRadius: '4px', 
                                            overflow: 'hidden' 
                                        }}>
                                            <div style={{ 
                                                height: '100%', 
                                                width: `${fileUploadProgress.percent}%`, 
                                                background: 'linear-gradient(90deg, #1890ff, #52c41a)',
                                                borderRadius: '4px',
                                                transition: 'width 0.3s ease'
                                            }} />
                                        </div>
                                        <div style={{ marginTop: 4, fontSize: '12px', color: '#1890ff' }}>
                                            {fileUploadProgress.percent}% completado
                                        </div>
                                    </div>
                                }
                                type="info"
                                showIcon
                                icon={<LoadingOutlined spin />}
                                style={{ marginBottom: 8 }}
                            />
                        )}

                        <Upload
                            beforeUpload={handleBeforeUpload}
                            onChange={handleFileChange}
                            onRemove={handleRemoveFile}
                            onPreview={handlePreviewFile}
                            fileList={attachedFiles}
                            accept=".pdf,.jpg,.jpeg,.png"
                            listType="picture"
                            maxCount={maxFiles}
                            multiple
                            disabled={uploadingFiles}
                        >
                            <Button 
                                icon={<PaperClipOutlined />} 
                                disabled={attachedFiles.length >= maxFiles || uploadingFiles}
                            >
                                Adjuntar Archivo ({attachedFiles.length}/{maxFiles})
                            </Button>
                        </Upload>

                        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                            <Space direction="vertical" size={0}>
                                <Text type="secondary">📎 Formatos permitidos: PDF, JPG, PNG</Text>
                                <Text type="secondary">📏 Tamaño máximo: 10MB por archivo</Text>
                                <Text type="secondary">📁 Máximo {maxFiles} archivos por pedido</Text>
                            </Space>
                        </div>

                        {attachedFiles.length > 0 && (
                            <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f0f5ff', borderRadius: '4px' }}>
                                <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>
                                    ✅ {attachedFiles.length} archivo(s) seleccionado(s)
                                </Text>
                                <div style={{ fontSize: '11px', color: '#52c41a', marginTop: 4 }}>
                                    Los archivos se subirán automáticamente al crear el pedido
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Form>

            {/* File Preview Modal */}
            <Modal
                open={filePreviewVisible}
                title="Vista Previa de Archivo"
                footer={[
                    <Button key="close" onClick={() => setFilePreviewVisible(false)}>
                        Cerrar
                    </Button>
                ]}
                onCancel={() => setFilePreviewVisible(false)}
                width={800}
            >
                {previewFile && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <div style={{ marginBottom: 16 }}>
                            {getFileIcon(previewFile.type)}
                        </div>
                        <Typography.Title level={5}>{previewFile.name}</Typography.Title>
                        {previewFile.url && previewFile.type?.startsWith('image/') ? (
                            <img 
                                src={previewFile.url} 
                                alt={previewFile.name} 
                                style={{ maxWidth: '100%', maxHeight: '500px', marginTop: '16px' }}
                            />
                        ) : (
                            <Alert
                                message="Vista previa no disponible"
                                description="La vista previa completa estará disponible después de cargar el archivo al servidor."
                                type="info"
                                showIcon
                                style={{ marginTop: 16 }}
                            />
                        )}
                    </div>
                )}
            </Modal>

            {/* Custom Product Modal */}
            <Modal
                open={customProductModalVisible}
                title={
                    <Space>
                        <AppstoreAddOutlined style={{ color: '#722ed1' }} />
                        <span>Agregar Producto/Servicio Personalizado</span>
                    </Space>
                }
                onCancel={() => {
                    setCustomProductModalVisible(false);
                    customProductForm.resetFields();
                }}
                width={600}
                footer={[
                    <Button 
                        key="cancel" 
                        onClick={() => {
                            setCustomProductModalVisible(false);
                            customProductForm.resetFields();
                        }}
                    >
                        Cancelar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => customProductForm.submit()}
                        style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                    >
                        Agregar Item
                    </Button>
                ]}
            >
                <Alert
                    message="Productos Personalizados"
                    description="Use esta opción para agregar servicios, productos únicos, o items que no están en la base de datos. Estos productos NO afectan el stock."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Form
                    form={customProductForm}
                    layout="vertical"
                    onFinish={handleAddCustomProduct}
                    initialValues={{
                        quantity: 1,
                        unit_price: 0,
                        iva_percentage: 21,
                        discount_percentage: 0,
                        notes: ''
                    }}
                >
                    <Form.Item
                        name="product_description"
                        label="Descripción del Producto/Servicio"
                        rules={[
                            { required: true, message: 'La descripción es requerida' },
                            { whitespace: true, message: 'La descripción no puede estar vacía' },
                            { min: 3, message: 'La descripción debe tener al menos 3 caracteres' }
                        ]}
                    >
                        <Input 
                            placeholder="Ej: Servicio de instalación, Repuesto especial, Flete..." 
                            maxLength={200}
                            showCount
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="quantity"
                                label="Cantidad"
                                rules={[
                                    { required: true, message: 'Requerido' },
                                    { type: 'number', min: 0.01, message: 'Debe ser mayor a 0' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0.01}
                                    step={1}
                                    precision={2}
                                    placeholder="1"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="unit_price"
                                label="Precio Unitario"
                                rules={[
                                    { required: true, message: 'Requerido' },
                                    { type: 'number', min: 0.01, message: 'Debe ser mayor a 0' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0.01}
                                    step={100}
                                    precision={2}
                                    prefix="$"
                                    placeholder="0.00"
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="iva_percentage"
                                label="IVA %"
                                rules={[{ required: true, message: 'Requerido' }]}
                            >
                                <Select placeholder="Seleccionar IVA">
                                    <Option value={21}>21%</Option>
                                    <Option value={10.5}>10.5%</Option>
                                    <Option value={0}>0% (Exento)</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="discount_percentage"
                                label="Descuento %"
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    max={99.99}
                                    step={1}
                                    precision={2}
                                    suffix="%"
                                    placeholder="0"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item
                                name="notes"
                                label="Notas (opcional)"
                            >
                                <Input 
                                    placeholder="Observaciones adicionales..."
                                    maxLength={500}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Live preview of the item */}
                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                            const qty = getFieldValue('quantity') || 0;
                            const price = getFieldValue('unit_price') || 0;
                            const iva = getFieldValue('iva_percentage') || 0;
                            const discount = getFieldValue('discount_percentage') || 0;
                            
                            const subtotal = qty * price;
                            const discountAmount = subtotal * (discount / 100);
                            const netSubtotal = subtotal - discountAmount;
                            const ivaAmount = netSubtotal * (iva / 100);
                            const total = netSubtotal + ivaAmount;

                            if (qty > 0 && price > 0) {
                                return (
                                    <Card size="small" style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
                                        <Row gutter={16}>
                                            <Col span={6}>
                                                <Text type="secondary">Subtotal:</Text>
                                                <div><Text strong>${subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Text></div>
                                            </Col>
                                            {discount > 0 && (
                                                <Col span={6}>
                                                    <Text type="secondary">Descuento:</Text>
                                                    <div><Text type="danger">-${discountAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Text></div>
                                                </Col>
                                            )}
                                            <Col span={6}>
                                                <Text type="secondary">IVA ({iva}%):</Text>
                                                <div><Text>${ivaAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Text></div>
                                            </Col>
                                            <Col span={6}>
                                                <Text type="secondary">Total:</Text>
                                                <div><Text strong style={{ color: '#52c41a', fontSize: '16px' }}>${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Text></div>
                                            </Col>
                                        </Row>
                                    </Card>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                </Form>
            </Modal>
        </Modal>
    );
};

export default OrderCreationModal;
